import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import {
  IPayment,
  API,
  ICustomer as ICustomerDEPRECATED,
  PermissionNames,
  PaymentStatus,
  PaymentTypeIds,
} from '@getgreenline/homi-shared'
import { ICustomer } from '@getgreenline/customers'
import { PaymentDetails } from '../../components/ExpandablePayment/PaymentDetails'
import { InventoryLogs } from '../../components/ExpandablePayment/InventoryLogs'
import { ViewReceiptButton } from '../../components/ExpandablePayment/ViewReceiptButton'
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import * as Tooltip from 'react-tooltip'
import { Tooltip as AntdTooltip, Spin } from 'antd-v4'
import { CancelPaymentModal } from './CancelPaymentModal'
import { CurrentUserStore } from '../../stores/CurrentUserStore'
import { CompletePaymentModal } from './CompletePaymentModal'
import { ChangePaymentTypeModal } from './ChangePaymentTypeModal'
import { FormError } from '../FormError'
import { message } from 'antd'
import { parseErrorMsg, isJsonString } from '../../utilities/helpers'
import { FindCustomerModal } from '../../containers/POS/Sale/FindCustomerModal'
import { paymentApi, BlazepayEnums, BlazepayPaymentOptionModels } from '@getgreenline/payments'
import { isNil, isNull } from 'lodash'
import { ViewWorldpayTransactionReceiptsButton } from './ViewWorldpayTransactionReceiptsButton'
import { THEME_COLORS } from '@getgreenline/shared'
import { BLAZE_COMPANY } from '../../constants/general'
import { LoyaltyModels } from '@getgreenline/loyalties'
import {
  formatPaymentOptionNames,
  getReceiptPaymentOptionNames,
} from '../../utilities/paymentsHelper'
import { CustomerSearch } from '@getgreenline/customer-search'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  payment: IPayment
  currentEmployeeId: any
  canEditSales: boolean
  canRefundSales: boolean
  canEditCustomer: boolean
  locationId: number | undefined
  hideButtons?: boolean
  onUpdate?: () => void
  refundPayment?: IPayment | undefined
  originalSalePayment?: IPayment | undefined
  loyaltyData?: LoyaltyModels.ILoyaltyData
  paymentOptions?: Map<
    BlazepayEnums.Provider,
    BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
  >
}

interface State {
  errorMessage?: string
  showCancelModal: boolean
  showCancelButton: boolean
  showCompleteModal: boolean
  showCompleteButton: boolean
  showInProgressButton: boolean
  showChangeModal: boolean
  payment: IPayment
  inventoryLogs?: IInventoryLog[]
  showSearchCustomerModal: boolean
  isLoading: boolean
  paymentOptionNames?: { [key: number]: string }
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ExpandablePaymentDetails extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      showCancelModal: false,
      showCancelButton: true,
      showCompleteModal: false,
      showCompleteButton: true,
      showInProgressButton: true,
      showChangeModal: false,
      payment: this.props.payment,
      showSearchCustomerModal: false,
      isLoading: false,
    }
  }

  async componentDidMount() {
    await this.setPaymentOptionNames()
  }

  setInProgress() {
    if (window.confirm('Are you sure?')) {
      const payment = this.state.payment
      this.props
        .currentCompanyStore!.inProgressPayment(payment.locationId, payment.id)
        .then(() => {
          this.setState({ showInProgressButton: false })
          this.getPaymentById(this.props.currentCompanyStore!.company!.id, payment.id)
          this.onUpdate()
        })
        .catch((error) => {
          this.setState({
            errorMessage: error.response.data.message || 'There was an error updating this sale',
          })
        })
    }
  }

  onUpdate() {
    if (this.props.onUpdate) {
      this.props.onUpdate()
    }
  }

  async getPaymentById(companyId: number, paymentId: string) {
    const payment = await API.getPaymentById(companyId, paymentId)
    this.setState({ payment: payment })
  }

  addCustomerToPayment = async (
    customer: ICustomerDEPRECATED | ICustomer | CustomerSearch.API.GlobalCustomer,
  ) => {
    const { currentCompanyStore } = this.props
    const { payment } = this.state

    try {
      await paymentApi.addCustomerToPayment({
        companyId: currentCompanyStore!.company!.id,
        locationId: payment.locationId,
        paymentId: payment.id,
        customerId: customer.id,
      })

      this.onUpdate()
      message.success('Updated')
      this.setState({ showSearchCustomerModal: false })
    } catch (error) {
      message.error(
        parseErrorMsg(error) || 'An error occurred while adding customer to the payment',
      )
    }
  }

  get shouldCancelButtonDisabled() {
    const currentUser = this.props.currentUserStore!.currentUser

    if (!currentUser) return true

    const isCompleteMonthSameAsCurrent = moment(this.props.payment.completeDate || moment()).isSame(
      moment(),
      'month',
    )

    return !isCompleteMonthSameAsCurrent && !currentUser.isAdmin
  }

  get cancelButton() {
    const payment = this.props.payment

    if (payment.status === 'cancelled' || payment.status === 'refunded' || !this.props.canEditSales)
      return

    return (
      <DropdownItem
        onClick={() => this.setState({ showCancelModal: true })}
        disabled={this.shouldCancelButtonDisabled || !isNil(this.props.refundPayment)}
        className={this.shouldCancelButtonDisabled && 'cursor-not-allowed'}
      >
        <span className='mr-2'>Cancel</span>
        <span className='fa fa-info-circle text-muted' data-tip data-for='cancel' />
        {this.cancelButtonTip}
      </DropdownItem>
    )
  }

  get cancelButtonTip() {
    if (this.props.refundPayment) {
      return (
        <Tooltip id='cancel' place='right' effect='solid'>
          <>
            This sale cannot be cancelled becuase it was refunded.
            <br />
            Please cancel refund {this.props.refundPayment.customId} to cancel this sale.
          </>
        </Tooltip>
      )
    } else
      return (
        <Tooltip id='cancel' place='right' effect='solid'>
          {this.shouldCancelButtonDisabled ? (
            <>
              You cannot cancel a sale from a previous month due to compliance issues.
              <br />
              Please refund a sale or contact support if you need assistance.
            </>
          ) : (
            <>
              Cancelled sales will not show up on reports.
              <br />
              Inventory will be returned.
            </>
          )}
        </Tooltip>
      )
  }

  get addCustomerButton() {
    const { canEditSales, currentEmployeeId } = this.props
    const { payment } = this.state

    if (!isNull(payment.customerId) || !canEditSales || !currentEmployeeId) {
      return
    }

    return (
      <DropdownItem onClick={() => this.setState({ showSearchCustomerModal: true })}>
        <span className='mr-2'>Add customer</span>
      </DropdownItem>
    )
  }

  get hasWorldpayTransaction() {
    const { transactions } = this.state.payment

    return transactions.some((transaction) => transaction.paymentTypeId === PaymentTypeIds.worldpay)
  }

  async setPaymentOptionNames() {
    const { payment } = this.state
    const { companyId, locationId } = payment

    try {
      this.setState({ isLoading: true })

      if (this.props.paymentOptions) {
        const paymentOptionNames = formatPaymentOptionNames(payment, this.props.paymentOptions)
        return this.setState({ paymentOptionNames })
      }

      const paymentOptionNames = await getReceiptPaymentOptionNames({
        companyId,
        locationId,
        payment,
      })
      return this.setState({ paymentOptionNames })
    } catch (error) {
      console.error(error)
      message.error(parseErrorMsg(error) || 'An error occurred while fetching payment option names')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  render() {
    const { currentUserStore, currentCompanyStore, currentEmployeeId, locationId } = this.props
    const { company } = currentCompanyStore!
    const userId = currentUserStore!.currentUser!.id
    const { payment, showSearchCustomerModal, isLoading, paymentOptionNames } = this.state

    const canAddCustomers = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_ADD_CUSTOMERS,
      company!.id,
    )
    const canEditCustomer = currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_CUSTOMERS,
      company!.id,
    )
    const canViewCustomerTotalSpend = !!currentUserStore?.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND,
      company!.id,
    )

    let completeButton: JSX.Element | undefined
    if (payment.status !== 'pending' && payment.status !== 'in progress') {
      completeButton = undefined
    } else if (this.props.canEditSales) {
      completeButton = (
        <DropdownItem onClick={() => this.setState({ showCompleteModal: true })}>
          <span className='mr-2'>Complete</span>
          <span className='fa fa-info-circle text-muted' data-tip data-for='complete' />
          <Tooltip id='complete' place='right' effect='solid'>
            <div>When completed, inventory will be removed (if it hasn't already).</div>
            <div>Only completed sales will show on reports.</div>
          </Tooltip>
        </DropdownItem>
      )
    }

    let inProgressButton: JSX.Element | undefined
    if (payment.status !== 'pending') {
      inProgressButton = undefined
    } else if (this.props.canEditSales) {
      inProgressButton = (
        <DropdownItem onClick={() => this.setInProgress()}>
          <span className='mr-2'>In progress</span>
          <span className='fa fa-info-circle text-muted' data-tip data-for='in-progress' />
          <Tooltip id='in-progress' place='right' effect='solid'>
            <div>Inventory will be removed.</div>
            <div>Only completed sales will show on reports.</div>
          </Tooltip>
        </DropdownItem>
      )
    }

    let changeButton: JSX.Element | undefined
    if (this.props.canEditSales && this.props.currentEmployeeId && !payment.refundPaymentId) {
      changeButton = (
        <DropdownItem onClick={() => this.setState({ showChangeModal: true })}>
          <span className='mr-2'>Change payment type</span>
          <span className='fa fa-info-circle text-muted' data-tip data-for='change-type' />
          <Tooltip id='change-type' place='right' effect='solid'>
            <div>Change between cash, debit, credit, or other payment types.</div>
          </Tooltip>
        </DropdownItem>
      )
    }

    let cancelledText: JSX.Element | undefined
    if (!this.props.canEditSales) {
      cancelledText = (
        <AntdTooltip trigger={['click']} placement='top' title='insufficient permissions'>
          <span>Actions</span>
        </AntdTooltip>
      )
    } else if (!this.props.currentEmployeeId && payment.status === PaymentStatus.CANCELLED) {
      cancelledText = (
        <AntdTooltip trigger={['click']} placement='top' title='no actions for cancelled payments'>
          <span>Actions</span>
        </AntdTooltip>
      )
    } else {
      cancelledText = <span>Actions</span>
    }

    return (
      <>
        {this.state.showCompleteModal ? (
          <CompletePaymentModal
            payment={payment}
            onClose={() => {
              this.setState({ showCompleteModal: false })
            }}
            onComplete={() => {
              this.setState({ showCompleteButton: false })
              this.getPaymentById(currentCompanyStore!.company!.id, payment.id)
              this.onUpdate()
            }}
          />
        ) : undefined}
        {this.state.showCancelModal ? (
          <CancelPaymentModal
            payment={payment}
            userId={userId}
            onClose={() => {
              this.setState({ showCancelModal: false })
            }}
            onCancel={() => {
              this.setState({ showCancelButton: false })
              this.getPaymentById(currentCompanyStore!.company!.id, payment.id)
              this.onUpdate()
            }}
          />
        ) : undefined}
        {this.state.showChangeModal && (
          <ChangePaymentTypeModal
            payment={payment}
            currentEmployeeId={currentEmployeeId!}
            onClose={() => this.setState({ showChangeModal: false })}
            onUpdate={() => {
              this.getPaymentById(currentCompanyStore!.company!.id, payment.id)
              this.setState({ showChangeModal: false })
              this.onUpdate()
            }}
          />
        )}
        <tr>
          <td colSpan={11}>
            <div
              className='mt-2 mb-2 ml-4 mr-4 expandable-payment-details'
              style={{
                padding: '1em',
                borderColor: THEME_COLORS.MONO.monodd,
                borderStyle: 'solid',
                borderWidth: 1,
              }}
            >
              {isLoading ? (
                <Spin size='small' className='flex-center' />
              ) : (
                <>
                  <div className='row'>
                    <div className='col-md-6'>
                      <PaymentDetails
                        canViewCustomerTotalSpend={canViewCustomerTotalSpend}
                        locationId={locationId}
                        payment={this.state.payment}
                        canEditCustomer={this.props.canEditCustomer}
                        loyaltyData={this.props.loyaltyData}
                        paymentOptionNames={paymentOptionNames}
                      />
                    </div>
                    <div className='col-md-6'>
                      <div className='text-muted'>
                        <div>
                          Created: {moment(payment.createDate).format('MMM DD, YYYY hh:mm a')}
                        </div>
                        {payment.completeDate ? (
                          <div>
                            Completed: {moment(payment.completeDate).format('MMM DD, YYYY hh:mm a')}
                          </div>
                        ) : (
                          <div>Not completed yet</div>
                        )}
                        {this.props.originalSalePayment &&
                          this.props.originalSalePayment.completeDate && (
                            <div className='font-weight-bold'>
                              Original Sale Date:{' '}
                              {moment(this.props.originalSalePayment.completeDate).format(
                                'MMM DD, YYYY hh:mm a',
                              )}
                            </div>
                          )}
                      </div>
                      <div className='text-muted'>Sale ID: {payment.customId}</div>
                      {currentCompanyStore &&
                        currentCompanyStore.company &&
                        payment.wooCommerceOrderJSON && (
                          <>
                            <div className='text-muted'>WooCommerce Order ID:</div>
                            <div className='text-muted mb-2'>
                              <a
                                href={`${currentCompanyStore.company.wooCommerceUrl}/wp-admin/post.php?post=${payment.wooCommerceOrderId}&action=edit`}
                                target='_blank'
                                rel='noreferrer'
                              >
                                {payment.wooCommerceOrderId}
                              </a>
                            </div>
                            <div className='text-muted'>WooCommerce billing:</div>
                            <pre className='text-muted mb-2'>
                              {isJsonString(payment.wooCommerceOrderJSON)
                                ? JSON.stringify(
                                    JSON.parse(payment.wooCommerceOrderJSON).billing,
                                    null,
                                    2,
                                  )
                                : 'Check the WooCommerce order'}
                            </pre>
                          </>
                        )}
                      <div className='text-muted mb-2'>
                        {BLAZE_COMPANY} ID: {payment.id}
                      </div>
                      <InventoryLogs payment={payment} />
                    </div>
                  </div>
                  <div className='d-flex'>
                    {!this.props.hideButtons && (
                      <UncontrolledDropdown>
                        <DropdownToggle
                          className='mr-2'
                          caret
                          color='primary'
                          disabled={
                            !this.props.canEditSales ||
                            (!this.props.currentEmployeeId &&
                              payment.status === PaymentStatus.CANCELLED)
                          }
                          style={{
                            cursor:
                              !this.props.canEditSales ||
                              (!this.props.currentEmployeeId &&
                                payment.status === PaymentStatus.CANCELLED)
                                ? 'not-allowed'
                                : 'pointer',
                          }}
                        >
                          {cancelledText}
                        </DropdownToggle>
                        <DropdownMenu className='background-shadow'>
                          {this.state.showInProgressButton && inProgressButton}
                          {this.state.showCompleteButton && completeButton}
                          {this.state.showCancelButton && this.cancelButton}
                          {changeButton}
                          {this.addCustomerButton}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    )}
                    <ViewReceiptButton
                      payment={payment}
                      loyaltyData={this.props.loyaltyData}
                      paymentOptionNames={paymentOptionNames}
                    />

                    {this.hasWorldpayTransaction && (
                      <ViewWorldpayTransactionReceiptsButton payment={payment} />
                    )}
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
        <FormError message={this.state.errorMessage} />

        {showSearchCustomerModal && (
          <FindCustomerModal
            show={true}
            canViewCustomerTotalSpend={canViewCustomerTotalSpend}
            isFromDashboard
            canAddCustomers={canAddCustomers}
            canEditCustomer={canEditCustomer}
            onHide={() => this.setState({ showSearchCustomerModal: false })}
            onSelect={this.addCustomerToPayment}
          />
        )}
      </>
    )
  }
}
